import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Logo } from "../Logo/Logo";
import { useLayoutConfig } from "./LayoutContext";
import type { BreadcrumbItem, NavItem, CTAConfig } from "./LayoutContext";
import { usePersonaDecision } from "../../personalization/store";
import { headerCtaFor } from "../../personalization/personaLinks";
import { BLOG_ENABLED } from "../../content/blogPosts/enabled";

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4 6h16M4 12h16M4 18h16"
      />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function ChevronDown({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

// ── Grouped navigation ──────────────────────────────────────────────────

interface NavGroup {
  label: string;
  items: NavItem[];
}

const DEFAULT_NAV_GROUPED: (NavItem | NavGroup)[] = [
  {
    label: "Products",
    items: [
      { label: "WritersProof", href: "/writersproof" },
      { label: "Scrivener MCP", href: "/scrivener-mcp" },
      { label: "WARM", href: "/warm" },
      { label: "AI Agents", href: "/agents" },
    ],
  },
  { label: "Compare", href: "/compare" },
  ...(BLOG_ENABLED ? [{ label: "Blog", href: "/blog" }] : []),
  {
    label: "Developers",
    items: [
      { label: "Protocol Spec", href: "/protocol" },
      { label: "Security Model", href: "/security-model" },
    ],
  },
  { label: "About", href: "/about" },
];

// Flat list for mobile menu
const DEFAULT_NAV: NavItem[] = [
  { label: "WritersProof", href: "/writersproof" },
  { label: "Scrivener MCP", href: "/scrivener-mcp" },
  { label: "WARM", href: "/warm" },
  { label: "AI Agents", href: "/agents" },
  { label: "Compare", href: "/compare" },
  ...(BLOG_ENABLED ? [{ label: "Blog", href: "/blog" }] : []),
  { label: "Protocol", href: "/protocol" },
  { label: "Developers", href: "/developers" },
  { label: "About", href: "/about" },
];

const DEFAULT_CTAS: CTAConfig[] = [
  { label: "Download Free", href: "/writersproof", variant: "primary" },
];

// ── Dropdown menu ───────────────────────────────────────────────────────

function NavDropdown({
  label,
  items,
  isActive,
}: {
  label: string;
  items: NavItem[];
  isActive: (href: string) => boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const anyActive = items.some((i) => i.href && isActive(i.href));

  return (
    <div
      ref={ref}
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 text-sm leading-none py-2.5 transition-colors border-b-2 border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20 rounded select-none ${
          anyActive
            ? "text-[var(--color-text-primary)] border-[var(--focus-ring)]"
            : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
        }`}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {label}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className="absolute top-full left-0 mt-2 py-2 min-w-[180px] rounded-xl backdrop-blur-xl border border-white/10 shadow-xl z-50"
          style={{
            backgroundColor:
              "color-mix(in srgb, var(--surface-dark, #0a0a0f) 95%, transparent)",
          }}
        >
          {items.map((item) => {
            const active = item.href && isActive(item.href);
            return (
              <Link
                key={item.label}
                to={item.href!}
                onClick={() => setOpen(false)}
                className={`block px-4 py-2.5 text-sm transition-colors ${
                  active
                    ? "text-[var(--color-text-primary)] bg-white/5"
                    : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-white/5"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Breadcrumbs ─────────────────────────────────────────────────────────

/* `pt-24` exists to clear the fixed header, which is out of flow. On a page that
   sets `fixedHeader: false` the header takes up its own 81px of flow first, so
   the same padding stacked on top of it and dropped the breadcrumb 81px lower
   than on every other page. The offset has to follow the header mode. */
function Breadcrumbs({
  items,
  clearsFixedHeader,
}: {
  items: BreadcrumbItem[];
  clearsFixedHeader: boolean;
}) {
  return (
    <nav
      className={`${clearsFixedHeader ? "pt-24" : "pt-4"} pb-2 section-x`}
      aria-label="Breadcrumb"
    >
      <div className="content-center text-left">
        <ol
          className="flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)]"
          itemScope
          itemType="https://schema.org/BreadcrumbList"
        >
          {items.map((item, i) => (
            <li key={i} className="flex items-center gap-1.5">
              {i > 0 && (
                <svg
                  className="w-3 h-3 text-[var(--color-text-secondary)] flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              )}
              <span
                itemProp="itemListElement"
                itemScope
                itemType="https://schema.org/ListItem"
              >
                {item.href ? (
                  <Link
                    to={item.href}
                    className="hover:text-[var(--color-text-primary)] transition-colors"
                    itemProp="item"
                  >
                    <span itemProp="name">{item.label}</span>
                  </Link>
                ) : (
                  <span
                    itemProp="name"
                    className="text-[var(--color-text-secondary)]"
                    aria-current="page"
                  >
                    {item.label}
                  </span>
                )}
                <meta
                  itemProp="position"
                  content={String(item.schemaPosition)}
                />
              </span>
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

// ── CTA button renderer ─────────────────────────────────────────────────

function CTAButton({
  cta,
  closeMenu,
}: {
  cta: CTAConfig;
  closeMenu?: () => void;
}) {
  const isPrimary = cta.variant === "primary";
  const isSecondary = cta.variant === "secondary";
  const isText = cta.variant === "text";

  // Canonical button primitives (index.css): token-driven, with rest/hover/
  // active/focus-visible/disabled built in. `text-sm` just makes the header CTA
  // compact; select-none keeps the label from being drag-selected.
  const primaryClass = "btn-primary text-sm select-none";
  const ghostClass = "btn-secondary text-sm select-none";
  const textClass =
    "text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20 rounded";

  const btnClass = isPrimary
    ? primaryClass
    : isSecondary
      ? ghostClass
      : textClass;
  const btnStyle = undefined;

  if (cta.scrollTo) {
    return (
      <button
        type="button"
        onClick={() => {
          const el = document.getElementById(cta.scrollTo!);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          closeMenu?.();
        }}
        className={btnClass}
        style={btnStyle}
      >
        {cta.label}
      </button>
    );
  }

  if (cta.onClick) {
    return (
      <button
        type="button"
        onClick={() => {
          cta.onClick!();
          closeMenu?.();
        }}
        className={btnClass}
        style={btnStyle}
      >
        {cta.label}
      </button>
    );
  }

  const isExternal = /^(mailto:|tel:|https?:\/\/)/.test(cta.href);

  if (isText) {
    return isExternal ? (
      <a
        href={cta.href}
        onClick={closeMenu}
        className={textClass}
        {...(cta.href.startsWith("http")
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
      >
        {cta.label}
      </a>
    ) : (
      <Link to={cta.href} onClick={closeMenu} className={textClass}>
        {cta.label}
      </Link>
    );
  }

  return isExternal ? (
    <a
      href={cta.href}
      onClick={closeMenu}
      className={btnClass}
      style={btnStyle}
      {...(cta.href.startsWith("http")
        ? { target: "_blank", rel: "noopener noreferrer" }
        : {})}
    >
      {cta.label}
    </a>
  ) : (
    <Link
      to={cta.href}
      onClick={closeMenu}
      className={btnClass}
      style={btnStyle}
    >
      {cta.label}
    </Link>
  );
}

// ── Main header ─────────────────────────────────────────────────────────

export function WritersLogicHeader() {
  const { config } = useLayoutConfig();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const hasOverride = !!(config.headerNavOverride || config.headerNavItems);
  const navItems =
    config.headerNavOverride ?? config.headerNavItems ?? DEFAULT_NAV;

  // Adapt the primary CTA to the inferred visitor persona, but only when the page hasn't
  // set its own CTAs AND we're confident (the header CTA is the money CTA, so a tentative
  // guess must not move it). Buyers always keep "Download Free". Fixed at load.
  const { persona, confident } = usePersonaDecision();
  const adaptedCta = confident ? headerCtaFor(persona) : null;
  const ctas =
    config.headerCTAs ??
    (adaptedCta
      ? [
          {
            label: adaptedCta.label,
            href: adaptedCta.href,
            variant: "primary" as const,
          },
        ]
      : DEFAULT_CTAS);
  const isFixed = config.fixedHeader !== false;

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  const closeMenu = useCallback(() => {
    setMobileMenuOpen(false);
    menuButtonRef.current?.focus();
  }, []);

  // Body scroll lock when mobile menu is open
  useEffect(() => {
    if (!mobileMenuOpen) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  // Escape key closes mobile menu
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMenu();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [mobileMenuOpen, closeMenu]);

  // Focus trap
  useEffect(() => {
    if (!mobileMenuOpen || !menuRef.current) return;
    const menu = menuRef.current;
    const sel =
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const firstFocusable = menu.querySelector<HTMLElement>(sel);
    if (firstFocusable) firstFocusable.focus();

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusable = Array.from(menu.querySelectorAll<HTMLElement>(sel));
      if (menuButtonRef.current) focusable.unshift(menuButtonRef.current);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleTab);
    return () => document.removeEventListener("keydown", handleTab);
  }, [mobileMenuOpen]);

  // Use grouped nav for desktop when not overridden
  const useGrouped = !hasOverride;

  return (
    <>
      <header
        role="banner"
        className={`${isFixed ? "fixed" : "relative"} top-0 left-0 right-0 z-50 backdrop-blur-lg bg-[rgba(10,10,15,0.9)]`}
        style={{
          backgroundColor:
            "color-mix(in srgb, var(--surface-dark, #0a0a0f) 90%, transparent)",
          borderBottom: "1px solid var(--border-color)",
        }}
      >
        {/* Skip link — first focusable, inside the banner landmark so all page
            content stays within landmarks (axe region rule). */}
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-gold-600 focus:text-slate-900 focus:rounded-lg"
        >
          Skip to main content
        </a>
        <nav
          className="max-w-7xl mx-auto section-x"
          aria-label="Main navigation"
        >
          <div className="flex items-center justify-between h-20">
            {/* Logo — official white icon + plain white text */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <Link to="/" aria-label="WritersLogic home">
                <Logo
                  size="lg"
                  showText
                  className="hidden sm:flex"
                  textClassName="text-[var(--color-text-primary)]"
                />
                <Logo size="lg" showText={false} className="flex sm:hidden" />
              </Link>
            </div>

            {/* Desktop nav — grouped with dropdowns */}
            <div className="hidden lg:flex items-center gap-5">
              {useGrouped
                ? // Grouped navigation with dropdowns
                  DEFAULT_NAV_GROUPED.map((item) =>
                    "items" in item ? (
                      <NavDropdown
                        key={item.label}
                        label={item.label}
                        items={item.items}
                        isActive={isActive}
                      />
                    ) : (
                      <Link
                        key={item.label}
                        to={item.href!}
                        className={`text-sm leading-none py-2.5 transition-colors border-b-2 border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20 rounded select-none ${
                          isActive(item.href!)
                            ? "text-[var(--color-text-primary)] border-[var(--focus-ring)]"
                            : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                        }`}
                      >
                        {item.label}
                      </Link>
                    ),
                  )
                : // Flat nav (overrides or org mode)
                  navItems.map((item) => {
                    const active =
                      !item.external &&
                      !item.scrollTo &&
                      item.href &&
                      isActive(item.href);
                    return item.external ? (
                      <a
                        key={item.label}
                        href={item.href!}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm leading-none py-2.5 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20 rounded select-none"
                      >
                        {item.label}
                      </a>
                    ) : item.scrollTo ? (
                      <button
                        type="button"
                        key={item.label}
                        onClick={() => {
                          const el = document.getElementById(item.scrollTo!);
                          if (el)
                            el.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                        }}
                        className="text-sm leading-none py-2.5 text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20 rounded select-none"
                      >
                        {item.label}
                      </button>
                    ) : (
                      <Link
                        key={item.label}
                        to={item.href!}
                        className={`text-sm leading-none py-2.5 transition-colors border-b-2 border-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20 rounded select-none ${
                          active
                            ? "text-[var(--color-text-primary)] border-[var(--focus-ring)]"
                            : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]"
                        }`}
                      >
                        {item.label}
                      </Link>
                    );
                  })}

              {/* CTA buttons */}
              {ctas.map((cta) => (
                <CTAButton key={cta.label} cta={cta} />
              ))}
            </div>

            {/* Mobile: show CTA + hamburger */}
            <div className="flex lg:hidden items-center gap-3">
              {ctas
                .filter((c) => c.variant === "primary")
                .slice(0, 1)
                .map((cta) => (
                  <CTAButton key={cta.label} cta={cta} />
                ))}
              <button
                type="button"
                ref={menuButtonRef}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20"
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileMenuOpen}
                aria-controls="mobile-nav-menu"
              >
                {mobileMenuOpen ? (
                  <XIcon className="w-6 h-6" />
                ) : (
                  <MenuIcon className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div
            id="mobile-nav-menu"
            ref={menuRef}
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation menu"
            className="lg:hidden absolute top-full left-0 right-0 backdrop-blur-xl border-b"
            style={{
              backgroundColor:
                "color-mix(in srgb, var(--surface-dark, #0a0a0f) 95%, transparent)",
              borderColor: "var(--border-color)",
            }}
          >
            <div className="max-w-7xl mx-auto px-4 py-4 space-y-1">
              {navItems.map((item) => {
                const active =
                  !item.external &&
                  !item.scrollTo &&
                  item.href &&
                  isActive(item.href);
                return item.external ? (
                  <a
                    key={item.label}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeMenu}
                    className="block px-4 py-3 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-white/5 transition-colors select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20"
                  >
                    {item.label}
                  </a>
                ) : item.scrollTo ? (
                  <button
                    type="button"
                    key={item.label}
                    onClick={() => {
                      const el = document.getElementById(item.scrollTo!);
                      if (el)
                        el.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      closeMenu();
                    }}
                    className="block w-full text-left px-4 py-3 rounded-lg text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-white/5 transition-colors select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20"
                  >
                    {item.label}
                  </button>
                ) : (
                  <Link
                    key={item.label}
                    to={item.href!}
                    onClick={closeMenu}
                    className={`block px-4 py-3 rounded-lg transition-colors select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/20 ${
                      active
                        ? "text-[var(--color-text-primary)] bg-white/10 border-l-4 border-[var(--focus-ring)]"
                        : "text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-white/5"
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
              {/* Mobile CTA */}
              {ctas.map((cta) => (
                <div key={cta.label} className="pt-2">
                  {cta.variant === "primary" ? (
                    /^(mailto:|tel:|https?:\/\/)/.test(cta.href) ? (
                      <a
                        href={cta.href}
                        onClick={closeMenu}
                        className="btn-primary w-full select-none"
                        {...(cta.href.startsWith("http")
                          ? { target: "_blank", rel: "noopener noreferrer" }
                          : {})}
                      >
                        {cta.label}
                      </a>
                    ) : (
                      <Link
                        to={cta.href}
                        onClick={closeMenu}
                        className="btn-primary w-full select-none"
                      >
                        {cta.label}
                      </Link>
                    )
                  ) : cta.variant === "secondary" ? (
                    <Link
                      to={cta.href}
                      onClick={closeMenu}
                      className="btn-secondary w-full select-none"
                    >
                      {cta.label}
                    </Link>
                  ) : (
                    <CTAButton cta={cta} closeMenu={closeMenu} />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </header>

      {config.breadcrumbs && (
        <Breadcrumbs items={config.breadcrumbs} clearsFixedHeader={isFixed} />
      )}
    </>
  );
}
